import { useState } from 'react';

import Button from '../../../../../components/Button/Button';
import Card from '../../../../../components/Card/Card';
import Column from '../../../../../components/Column/Column';
import Columns from '../../../../../components/Columns/Columns';
import DialogModal, {
    DialogModalProps,
} from '../../../../../components/DialogModal/DialogModal';
import PaddedBox from '../../../../../components/PaddedBox/PaddedBox';
import RichText from '../../../../../components/RichText/RichText';
import Row from '../../../../../components/Row/Row';
import Stack from '../../../../../components/Stack/Stack';
import Title from '../../../../../components/Title/Title';
import Well from '../../../../../components/Well/Well';
import useTrans from '../../../../../hooks/ui/useTrans';
import MediaCarousel from '../../../../components/MediaCarousel/MediaCarousel';
import BoatModelWithMedia from '../../../../types/BoatModelWithMedia';
import BoatModelSpecs from '../BoatModelSpecs/BoatModelSpecs';
import NumberOfBoatsSelect from '../NumberOfBoatsSelect/NumberOfBoatsSelect';

import styles from './BoatModelInfoModal.module.css';

type BoatModelInfoModalProps = {
    boatModel: BoatModelWithMedia;
    onSelect: (boatModel: BoatModelWithMedia, quantity: number) => void;
} & Omit<DialogModalProps, 'children' | 'width'>;

const BoatModelInfoModal = ({
    boatModel,
    onSelect,
    ...modalProps
}: BoatModelInfoModalProps) => {
    const trans = useTrans('CustomerArea.BookingForm.BoatModelInfoModal.');
    const [quantity, setQuantity] = useState(1);
    const hasMedia = boatModel.media && boatModel.media.length > 0;

    return (
        <DialogModal
            {...modalProps}
            isOpen
            backdrop="none"
            width="regular"
            unstyled
        >
            <Card horizontalPadding="none" verticalPadding="none">
                {hasMedia && (
                    <div className={styles.Carousel}>
                        <MediaCarousel
                            media={boatModel.media}
                            variant="fullSize"
                        >
                            <Title variant="area" lineHeight="small">
                                {boatModel.name}
                            </Title>
                        </MediaCarousel>
                    </div>
                )}
                <PaddedBox borderRadius={hasMedia ? 'none' : 'top'}>
                    {boatModel.specs.length > 0 ? (
                        <Columns collapseBelow="tablet" gap="xlarge">
                            <Column width="1/2">
                                <RichText html={boatModel.description} />
                            </Column>
                            <Column width="1/2">
                                <Well>
                                    <Stack>
                                        <Title>
                                            {trans('SpecificationsTitle')}
                                        </Title>
                                        <BoatModelSpecs
                                            specs={boatModel.specs}
                                        />
                                    </Stack>
                                </Well>
                            </Column>
                        </Columns>
                    ) : (
                        <RichText html={boatModel.description} />
                    )}
                </PaddedBox>
                <PaddedBox
                    variant="tint"
                    borderRadius="bottom"
                    verticalPadding="small"
                >
                    <Row vertical="middle" horizontal="right" flex>
                        {boatModel?.maxSelectableBoats && (
                            <NumberOfBoatsSelect
                                variant="white-no-border"
                                max={boatModel.maxSelectableBoats}
                                value={quantity}
                                onChange={setQuantity}
                                size="small"
                            />
                        )}
                        <Button onClick={() => onSelect(boatModel, quantity)}>
                            {trans('ChooseButton')}
                        </Button>
                    </Row>
                </PaddedBox>
            </Card>
        </DialogModal>
    );
};

export default BoatModelInfoModal;
